import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from '../endpoints';
/**
 * Configuration interface for the useSessionTimeout hook.
 * @interface SessionTimeoutConfig
 * @property {number} inactivityLimit - Time in milliseconds before logout due to user inactivity.
 * @property {number} forcedLogoutLimit - Time in milliseconds before forced logout (e.g., for expired tokens).
 * @property {(redirectUri: string, idTokenHint: string) => string} buildLogoutUrl - Function to construct the logout URL.
 * @property {Storage} [storage=sessionStorage] - Storage object to use for session data (defaults to sessionStorage).
 */
interface SessionTimeoutConfig {
  inactivityLimit: number; 
  forcedLogoutLimit: number; 
  buildLogoutUrl: (redirectUri: string, idTokenHint: string) => string;
  storage?: Storage;
}

const useSessionTimeout = ({
  inactivityLimit,
  forcedLogoutLimit,
  storage = sessionStorage,
}: SessionTimeoutConfig) => {
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const forcedLogoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const navigate = useNavigate();
/**
   * Handles session logout by clearing session data and redirecting to the logout URL.
   */
  const handleLogoutSession = useCallback(() => {

      storage.removeItem('access_token');
      storage.removeItem('refresh_token');
      storage.removeItem('id_token');
      storage.removeItem('pkceCodeVerifier');
        navigate(PATH, { replace: true });
    
  }, [storage]);
 /**
   * Resets the inactivity timer, clearing the previous timeout and setting a new one.
   */
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = setTimeout(() => {
      handleLogoutSession();
    }, inactivityLimit);
  }, [handleLogoutSession, inactivityLimit]);
  /**
   * Sets up the forced logout timer, clearing the previous timeout and setting a new one.
   */
  const setupForcedLogoutTimer = useCallback(() => {
    if (forcedLogoutTimeoutRef.current) {
      clearTimeout(forcedLogoutTimeoutRef.current);
    }
    forcedLogoutTimeoutRef.current = setTimeout(() => {
      handleLogoutSession();
    }, forcedLogoutLimit);
  }, [handleLogoutSession, forcedLogoutLimit]);
/**
   * Sets up event listeners for user activity to reset the inactivity timer.
   * Returns a cleanup function to remove the listeners.
   * @private
   * @returns {() => void} Cleanup function to remove event listeners.
   */
  const setupActivityListeners = useCallback(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [resetInactivityTimer]);
 /**
   * Effect to initialize timers and activity listeners, with cleanup on unmount.
   */
  useEffect(() => {
    resetInactivityTimer();
    setupForcedLogoutTimer();
    const cleanupActivityListeners = setupActivityListeners();

    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (forcedLogoutTimeoutRef.current) {
        clearTimeout(forcedLogoutTimeoutRef.current);
      }
      cleanupActivityListeners();
    };
  }, [resetInactivityTimer, setupActivityListeners, setupForcedLogoutTimer]);
};

export default useSessionTimeout;